import type { CityConfig } from "../city-template/types";

export const ellsworthConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Maine Vital Records (Birth, Death, Marriage, Divorce)", href: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/", description: "Official Maine CDC vital records office in Augusta" },
    { label: "Hancock County — Marriage &amp; Vital Records", href: "https://www.hancockcountymaine.gov/", description: "Hancock County — local marriage licenses and vital records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Maine CDC for online vital record orders" },
  ],
  city: "Ellsworth",
  state: "Maine",
  stateAbbr: "ME",
  slug: "ellsworth",
  route: "/cities/ellsworth",
  title: "Emergency Passport Ellsworth, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Ellsworth, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Ellsworth, Bar Harbor, Blue Hill, and Hancock County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Ellsworth, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Ellsworth and Downeast Maine travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Ellsworth%20Maine%20charming%20coastal%20town%20with%20Union%20River%2C%20historic%20downtown%20brick%20buildings%2C%20gateway%20to%20Acadia%20National%20Park%2C%20beautiful%20green%20trees%20and%20New%20England%20architecture%2C%20warm%20afternoon%20light%2C%20professional%20photography%2C%20scenic%20small%20harbor%20and%20riverside%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20rural%20Maine%20coast%20scenery&width=1800&height=700&seq=ellsworth-me-hero-001&orientation=landscape",
  heroImageAlt: "Ellsworth Maine historic downtown along the Union River near Acadia National Park",
  geoPosition: "44.5432;-68.4194",
  geoPlacename: "Ellsworth, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 210 miles (4 hours) from Ellsworth, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Ellsworth", zips: "04605" },
    { name: "Bar Harbor", zips: "04609" },
    { name: "Blue Hill", zips: "04614" },
    { name: "Bucksport", zips: "04416" },
    { name: "Sullivan", zips: "04664" },
    { name: "Surry", zips: "04684" },
    { name: "Hancock", zips: "04640" },
    { name: "Orland", zips: "04472" },
    { name: "Castine", zips: "04421" },
    { name: "Sorrento", zips: "04677" },
    { name: "Franklin", zips: "04634" },
    { name: "Trenton", zips: "04605" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Ellsworth departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Bangor", route: "/cities/bangor" },
    { name: "Emergency passport help in Portland", route: "/cities/portland-me" },
    { name: "Urgent passport services in Augusta", route: "/cities/augusta-me" },
    { name: "Same-day passport help in Bar Harbor", route: "/cities/bar-harbor" },
    { name: "Expedited passport services in Brewer", route: "/cities/brewer" },
    { name: "Passport courier services in Old Town", route: "/cities/old-town" },
    { name: "Rush passport help in Lewiston", route: "/cities/lewiston" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Ellsworth, Maine remotely via secure courier. We do not have a walk-in office in Ellsworth — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Ellsworth travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving all of Hancock and eastern Waldo Counties",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Ellsworth — the gateway city to Acadia National Park and the heart of Downeast Maine. From downtown Ellsworth along the Union River to the coastal villages of Bar Harbor, Blue Hill, and Castine — we help Downeast travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all of Hancock County including Ellsworth, Bar Harbor, Blue Hill, Bucksport, Sullivan, Surry, Hancock, Orland, Castine, and Trenton. We also serve travelers heading to Mount Desert Island and the Schoodic Peninsula.",
  howItWorksIntro: "Ellsworth travelers complete everything online and ship documents overnight. No need to make the long 4-hour drive to Boston. Our emergency and expedited passport process is designed for Downeast Maine travelers who need fast, reliable results.",
  mistakesIntro: "Ellsworth and Bar Harbor area travelers frequently depart for Canadian cruises, European tours, and international fishing trips. Passport delays can ruin these carefully planned trips. Here are the most common mistakes we see.",
  testimonialsIntro: "Ellsworth travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Ellsworth? Here are the most common ones we hear from Downeast Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Ellsworth international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Diane M.",
      location: "Ellsworth, ME",
      rating: 5,
      text: "We booked a Canadian cruise from Bar Harbor and realized my passport expired two weeks before departure. Fast Passport Center processed my renewal in 48 hours. I was amazed they could help us from Downeast Maine.",
      service: "Emergency Passport",
    },
    {
      name: "Steven B.",
      location: "Bar Harbor, ME",
      rating: 5,
      text: "Seasonal worker who needs to travel for international tourism work. Fast Passport Center has been my reliable partner for renewals every year. Always fast, always professional.",
      service: "Passport Renewal",
    },
    {
      name: "Nancy K.",
      location: "Blue Hill, ME",
      rating: 5,
      text: "Lost my passport before my daughter's wedding in Ireland. I was terrified. Fast Passport Center got me a replacement in 2 days. I arrived at the wedding with time to spare.",
      service: "Lost Passport",
    },
    {
      name: "Michael R.",
      location: "Bucksport, ME",
      rating: 5,
      text: "International fishing trip to Iceland was in 4 days and my passport was nowhere to be found. Fast Passport Center came through in under 48 hours. Incredible service for the Hancock County area.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "ellsworth-1",
      name: "Ellsworth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "32 Franklin St",
      city: "Ellsworth",
      state: "ME",
      zip: "04605",
      phone: "(207) 667-2561",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Ellsworth",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "ellsworth-2",
      name: "Bar Harbor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "55 Cottage St",
      city: "Bar Harbor",
      state: "ME",
      zip: "04609",
      phone: "(207) 288-3318",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "16 miles from Ellsworth",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Mount Desert Island residents. Call ahead to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "ellsworth-3",
      name: "Blue Hill Town Office",
      type: "Town Office Passport Acceptance",
      address: "18 Union St",
      city: "Blue Hill",
      state: "ME",
      zip: "04614",
      phone: "(207) 374-2281",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "20 miles from Ellsworth",
      website: "https://www.bluehillme.gov/",
      websiteLabel: "Blue Hill Town Office",
      notes: "Good option for Blue Hill and Brooksville residents. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "ellsworth-4",
      name: "Bucksport Town Office",
      type: "Town Office Passport Acceptance",
      address: "50 Main St",
      city: "Bucksport",
      state: "ME",
      zip: "04416",
      phone: "(207) 469-7368",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "18 miles from Ellsworth",
      website: "https://www.bucksport.maine.gov/",
      websiteLabel: "Bucksport Town Office",
      notes: "Popular for eastern Waldo County. Call ahead to schedule.",
      icon: "ri-government-line",
    },
    {
      id: "ellsworth-5",
      name: "Castine Town Office",
      type: "Town Office Passport Acceptance",
      address: "67 Court St",
      city: "Castine",
      state: "ME",
      zip: "04421",
      phone: "(207) 326-4502",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "25 miles from Ellsworth",
      website: "https://www.castine.me.us/",
      websiteLabel: "Castine Town Office",
      notes: "Small town office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-government-line",
    },
    {
      id: "ellsworth-6",
      name: "Bangor Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "22 Second St",
      city: "Bangor",
      state: "ME",
      zip: "04401",
      phone: "(207) 990-0151",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles from Ellsworth",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility with more appointment slots. Good backup option.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Ellsworth acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Ellsworth applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Ellsworth families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Ellsworth travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Ellsworth." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Ellsworth address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Ellsworth, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Ellsworth in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Ellsworth travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Ellsworth, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Ellsworth residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Ellsworth, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Ellsworth with no office visit required. We serve all of Hancock County and Downeast Maine. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Ellsworth, Maine?",
      a: "The fastest way to get an urgent passport in Ellsworth is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing or even government expedited mail.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Ellsworth?",
      a: "Popular options include the Ellsworth Post Office (04605), Bar Harbor Post Office (04609), Blue Hill Town Office (04614), and Bucksport Town Office (04416). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Bar Harbor, Blue Hill, or Bucksport?",
      a: "We serve all of Hancock County including Bar Harbor, Blue Hill, Bucksport, Sullivan, Surry, and Castine. Our overnight shipping labels work from any UPS location, so your location on the coast or inland doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Ellsworth?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Ellsworth?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in Ellsworth and Bar Harbor offer passport photo services. We review your photos before submission.",
    },
    {
      q: "What documents do I need for a first-time passport in Ellsworth?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Ellsworth?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Ellsworth families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "How far is Ellsworth from the Boston Passport Agency?",
      a: "Ellsworth is approximately 210 miles from the Boston Passport Agency — about a 4-hour drive. With Fast Passport Center, you avoid that long trip entirely. We courier your documents directly to State Department facilities.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Ellsworth", address: "32 Franklin St, Ellsworth, ME 04605", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com" },
    { name: "Walgreens — Ellsworth", address: "5 Downeast Hwy, Ellsworth, ME 04605", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com" },
    { name: "UPS Store — Ellsworth", address: "32 Franklin St, Ellsworth, ME 04605", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com" },
    { name: "Walmart — Ellsworth", address: "259 High St, Ellsworth, ME 04605", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com" },
    { name: "CVS Pharmacy — Bar Harbor", address: "55 Cottage St, Bar Harbor, ME 04609", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com" },
    { name: "Walgreens — Bangor", address: "650 Main St, Bangor, ME 04401", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com" },
    { name: "Rite Aid — Ellsworth", address: "25 Downeast Hwy, Ellsworth, ME 04605", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com" },
    { name: "Walgreens — Blue Hill", address: "18 Union St, Blue Hill, ME 04614", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com" },
  ],
  dmvLocations: [
    {
      name: "BMV — Ellsworth",
      address: "32 Franklin St, Ellsworth, ME 04605",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Bar Harbor",
      address: "55 Cottage St, Bar Harbor, ME 04609",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Limited hours. Online appointment booking recommended.",
      featured: false,
    },
    {
      name: "BMV — Bangor",
      address: "22 Second St, Bangor, ME 04401",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Best option for non-resident services.",
      featured: false,
    },
    {
      name: "BMV — Bucksport",
      address: "50 Main St, Bucksport, ME 04416",
      hours: "Mon–Fri: 8AM–4:00PM",
      note: "Basic services available. Call ahead.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "30 miles from Ellsworth (40 minutes)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport. The closest major airport for Ellsworth and Downeast travelers with domestic flights and seasonal international connections.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "130 miles from Ellsworth (2.5 hours)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport. A longer drive but offers more direct flights and international connections.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
    {
      code: "BHB",
      name: "Hancock County-Bar Harbor Airport",
      distance: "8 miles from Ellsworth (15 minutes)",
      address: "115 Caruso Dr, Trenton, ME 04605",
      note: "Small regional airport with Cape Air service to Boston Logan. Useful for connecting to international flights.",
      flightUrl: "https://www.bhbairport.com/",
      flightLabel: "Bar Harbor Airport",
      featured: true,
    },
  ],
};